import { format } from 'date-fns'
import { ExternalLink, Truck } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { FulfillmentStatusBadge } from './FulfillmentStatusBadge'
import type { FulfillmentOrder } from '../api/fulfillmentApi'

interface FulfillmentTableProps {
  orders: FulfillmentOrder[]
  onRowClick: (order: FulfillmentOrder) => void
}

function money(amount: string | number | null | undefined, currency: string | null) {
  if (amount === null || amount === undefined) return '—'
  return `${currency || ''} ${Number(amount).toFixed(2)}`.trim()
}

export function FulfillmentTable({ orders, onRowClick }: FulfillmentTableProps) {
  if (!orders.length) {
    return (
      <div className="rounded-md border py-16 text-center text-muted-foreground">
        No fulfillment orders found.
      </div>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Provider Order</TableHead>
            <TableHead>Shopify #</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center">Items</TableHead>
            <TableHead>Recipient</TableHead>
            <TableHead className="text-right">Cost</TableHead>
            <TableHead>Tracking</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow
              key={o.id}
              className="cursor-pointer"
              onClick={() => onRowClick(o)}
            >
              <TableCell className="font-mono text-xs">{o.providerOrderId || '—'}</TableCell>
              <TableCell>
                <span className="inline-flex items-center gap-1">
                  {o.merchantReference || '—'}
                  {o.shopifyOrderId && (
                    <ExternalLink className="h-3 w-3 text-muted-foreground" aria-label="linked to Shopify order" />
                  )}
                </span>
              </TableCell>
              <TableCell><FulfillmentStatusBadge status={o.status} /></TableCell>
              <TableCell className="text-center">{o.itemCount ?? '—'}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span>{o.recipient?.name || '—'}</span>
                  <span className="text-xs text-muted-foreground">{o.recipient?.address?.countryCode || ''}</span>
                </div>
              </TableCell>
              <TableCell className="text-right">{money(o.totalCost, o.currency)}</TableCell>
              <TableCell>
                {o.trackingNumber ? (
                  <span className="inline-flex items-center gap-1 text-xs">
                    <Truck className="h-3 w-3 text-muted-foreground" />
                    {o.carrierName ? `${o.carrierName}` : o.trackingNumber}
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {o.providerCreatedAt ? format(new Date(o.providerCreatedAt), 'dd MMM yyyy') : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
